'use client';

import classNames from 'classnames';
import { useRouter } from 'next/navigation';
import React, { useRef } from 'react';
import { CmsImage } from '../../../components/CmsImage';
import { FeLink } from '../../../components/FeLink';
import Profile from '../../../components/icons/Profile';
import { MaterialIcon } from '../../../components/MaterialIcon';
import { useRedirect } from '../../../hooks/useRedirect';
import { useIdentity } from '../../../modules/auth';
import { CmsSettings } from '../../../types/cmsSettings';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { getHomePagePath } from '../../../utils/routing';
import { NAVIGATION_TRANSLATIONS } from '../i18n';
import { CmsNavigation } from '../types';
import { getLogoImageSizing } from '../utils/logoImage';
import DropdownMenu, { topLevelIndex } from './DropdownMenu';
import { LanguageToggle, LanguageToggleTarget } from './LanguageToggle';
import Sidebar, { SidebarRef } from './Sidebar';

export interface TheHeaderProps {
  host: string;
  navigation: CmsNavigation;
  settings: CmsSettings;
  pushDataLayer: PushDataLayerFn;
  authEndpoint?: string;
  /** No toggle when absent; which pages get one is the app's call (CMS-2008). */
  languageToggle?: LanguageToggleTarget;
}

const TheHeader: React.FC<TheHeaderProps> = (props) => {
  const {
    brand: theme,
    lang: current_locale,
    langPrefix,
  } = useCmsClientContext();
  const staticText = NAVIGATION_TRANSLATIONS.get(current_locale);

  const {
    settings,
    navigation,
    host,
    pushDataLayer,
    authEndpoint: auth_endpoint,
    languageToggle,
  } = props;
  const hasLanguageToggle = !!languageToggle;

  const {
    site_logo: { url: appLogo, alternativeText: appLogoAlt },
  } = settings;

  const identity = useIdentity();
  const loggedIn = !!identity;
  const sidebarRef = useRef<SidebarRef>(null);

  const redirect = useRedirect();
  const router = useRouter();

  const getHostName = () => {
    return `https://${host}`;
  };

  const handleUserIcon = () => {
    const openUrl = (url: string) => {
      redirect(url);
    };
    let url;
    let hostname;
    switch (theme) {
      case 'singsaver':
      case 'moneymax':
        url = `/user/account`;
        break;
      case 'moneyhero':
        // eslint-disable-next-line no-case-declarations
        let tmpCurrentLocale = current_locale.split('-')[0];
        hostname = getHostName();
        if (
          tmpCurrentLocale.length &&
          !tmpCurrentLocale.startsWith('/') &&
          !hostname.endsWith('/')
        ) {
          tmpCurrentLocale = `/${tmpCurrentLocale}`;
        }
        url = `${tmpCurrentLocale}/user/account`;
        break;
      case 'money101':
        url = `/首頁`;
        break;
      default:
        break;
    }
    if (url) {
      openUrl(url);
    }
  };

  const handleLogout = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (auth_endpoint) {
      const url = new URL(`${auth_endpoint}/logout`, window.location.href);
      fetch(url, { method: 'POST', credentials: 'include' })
        .then((response) => {
          if (response.ok) {
            window.location.reload();
          } else {
            console.error('Logout failed:', response.statusText);
          }
        })
        .catch((error) => {
          console.error('Error during logout:', error.message);
        });
    }
  };

  const handleLogin = () => {
    if (!identity) {
      if (theme === 'moneyhero') {
        const tmpCurrentLocale = current_locale.split('-')[0];
        redirect(
          `/${tmpCurrentLocale}/login?redirect_uri=${encodeURIComponent(window.location.href)}`,
        );
      } else {
        redirect(
          `/login?redirect_uri=${encodeURIComponent(window.location.href)}`,
        );
      }
    }
  };

  const openSidebar = () => {
    if (sidebarRef.current) {
      sidebarRef.current.open();
    }
  };

  return (
    <nav
      data-component="the-header"
      // eslint-disable-next-line tailwindcss/no-arbitrary-value
      className={classNames(
        'relative z-[var(--z-index-header)] drop-shadow-lg bg-white',
      )}
      id="navbar-parent"
      style={{ lineHeight: '24px' }}
    >
      <div className="mx-auto max-w-screen-2xl px-4 py-2 lg:px-10 lg:py-0">
        <div className="flex items-center justify-between px-0 lg:h-18">
          {/* Hidden by default; revealed by `data-header-back-show` (see cms.css)
              for full-screen flows like apply-v3 Modes 2/3. */}
          <button
            type="button"
            onClick={() => router.back()}
            className="hidden"
            aria-label="Back"
            data-header-item="back"
          >
            <MaterialIcon icon="arrow_left_alt" px={24} />
          </button>
          <button
            type="button"
            onClick={openSidebar}
            className="lg:hidden"
            aria-label="Open menu"
            data-header-item="hamburger"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
              />
            </svg>
          </button>

          {/* Beside the language toggle the logo gives way on a narrow phone (min-w-0) and keeps a
              gap to it. */}
          <div
            className={classNames(
              'flex items-center gap-10',
              hasLanguageToggle && 'mr-2 min-w-0',
            )}
            data-header-item="logo"
          >
            <FeLink
              href={getHomePagePath(langPrefix)}
              onClick={() =>
                pushDataLayer({
                  event: 'navigate',
                  component_type: 'top-nav',
                  component_title: 'Top Nav_Logo',
                  action: 'click',
                })
              }
            >
              <CmsImage
                src={appLogo || '/img/logo.webp'}
                alt={appLogoAlt || 'logo'}
                {...getLogoImageSizing(settings.site_logo)}
                // Above the fold on every page.
                loading="eager"
                className={classNames(
                  'h-6 w-auto sm:h-8 lg:h-8',
                  hasLanguageToggle && 'max-w-full object-contain object-left',
                )}
              />
            </FeLink>
          </div>

          <Sidebar
            ref={sidebarRef}
            navigation={navigation}
            settings={settings}
            pushDataLayer={pushDataLayer}
          />

          <DropdownMenu
            dropdownMenu={navigation}
            pushDataLayer={pushDataLayer}
          />

          <div
            className={classNames('flex items-center gap-4')}
            data-header-item="actions"
          >
            {/* In the bar rather than the mobile menu, so it is never shown twice. */}
            {languageToggle && (
              <LanguageToggle
                {...languageToggle}
                pushDataLayer={pushDataLayer}
                // The place right after the desktop menu's top-level items.
                index={topLevelIndex(navigation.items?.length ?? 0)}
              />
            )}
            {!loggedIn ? (
              <button
                type="button"
                className="hidden font-semibold text-gray-600 text-lg leading-normal lg:inline-block"
                onClick={handleLogin}
              >
                {staticText.login}
              </button>
            ) : theme === 'moneyhero' || theme === 'moneymax' ? (
              <button
                type="button"
                className="hidden font-semibold text-gray-600 text-lg leading-normal lg:inline-block"
                onClick={handleLogout}
              >
                {staticText.logout}
              </button>
            ) : null}

            {!loggedIn ? (
              <button
                type="button"
                className="flex size-8 items-center justify-center rounded-full bg-primary-800"
                onClick={handleLogin}
                aria-label="Log in"
              >
                <Profile className="size-6" />
              </button>
            ) : (
              <button
                type="button"
                className="flex size-8 items-center justify-center rounded-full bg-primary-800"
                onClick={handleUserIcon}
                aria-label="User profile"
              >
                <Profile className="size-6" />
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export { TheHeader };
